import {
  DotsThreeIcon,
  FileTextIcon,
  HeadsetIcon,
  HouseIcon,
  LayoutIcon,
  ReadCvLogoIcon,
  SparkleIcon,
  WalletIcon,
  XIcon,
  type Icon,
} from "@phosphor-icons/react";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { Link, useLocation } from "react-router";

import { useDialog } from "@/client/stores/dialog";

import { USER_SPACE_NAVIGATION, USER_SPACE_PATHS } from "../../config/navigation";
import type { NavigationItem } from "../../types/navigation";

type MobileNavigationProps = {
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
};

type MobileTab = {
  label: string;
  path?: string;
  icon: Icon;
  matchingPaths?: string[];
  action?: "open-menu";
};

const mobileTabs: MobileTab[] = [
  {
    label: "Accueil",
    path: USER_SPACE_PATHS.dashboard,
    icon: HouseIcon,
  },
  {
    label: "Documents",
    path: USER_SPACE_PATHS.resumes,
    icon: FileTextIcon,
    matchingPaths: [USER_SPACE_PATHS.resumes, USER_SPACE_PATHS.coverLetters],
  },
  {
    label: "Modèles",
    path: USER_SPACE_PATHS.resumeTemplates,
    icon: LayoutIcon,
    matchingPaths: [USER_SPACE_PATHS.resumeTemplates, USER_SPACE_PATHS.coverLetterTemplates],
  },
  {
    label: "Plus",
    icon: DotsThreeIcon,
    action: "open-menu",
  },
];

const isPathActive = (pathname: string, path: string) => {
  if (path === USER_SPACE_PATHS.dashboard) {
    return pathname === path || pathname === `${path}/`;
  }

  return pathname === path || pathname.startsWith(`${path}/`);
};

const isTabActive = (pathname: string, tab: MobileTab, menuOpen: boolean) => {
  if (tab.action === "open-menu") {
    return menuOpen;
  }

  if (tab.matchingPaths) {
    return tab.matchingPaths.some((path) => isPathActive(pathname, path));
  }

  return tab.path ? isPathActive(pathname, tab.path) : false;
};

const MenuItem = ({ item, pathname }: { item: NavigationItem; pathname: string }) => {
  const IconComponent = item.icon;
  const isActive = isPathActive(pathname, item.path);

  if (item.disabled) {
    return (
      <div
        aria-disabled="true"
        className={cn(
          "flex min-h-16 cursor-not-allowed items-center gap-3",
          "rounded-2xl bg-slate-50 px-4",
          "text-slate-400 opacity-60",
        )}
      >
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-white">
          <IconComponent aria-hidden size={21} />
        </span>

        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{item.label}</span>

          {item.description && (
            <span className="mt-0.5 block truncate text-xs">{item.description}</span>
          )}
        </span>
      </div>
    );
  }

  return (
    <SheetClose asChild>
      <Link
        to={item.path}
        aria-current={isActive ? "page" : undefined}
        className={cn(
          "flex min-h-16 items-center gap-3 rounded-2xl border px-4",
          "transition-colors focus-visible:outline-none",
          "focus-visible:ring-2 focus-visible:ring-indigo-500",
          isActive
            ? "border-indigo-200 bg-indigo-50 text-indigo-700"
            : "border-slate-100 bg-white text-slate-700 hover:bg-slate-50",
        )}
      >
        <span
          className={cn(
            "grid size-10 shrink-0 place-items-center rounded-xl",
            isActive ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600",
          )}
        >
          <IconComponent aria-hidden size={21} weight={isActive ? "fill" : "regular"} />
        </span>

        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{item.label}</span>

          {item.description && (
            <span
              className={cn(
                "mt-0.5 block truncate text-xs",
                isActive ? "text-indigo-500" : "text-slate-500",
              )}
            >
              {item.description}
            </span>
          )}
        </span>
      </Link>
    </SheetClose>
  );
};

export const MobileNavigation = ({ menuOpen, onMenuOpenChange }: MobileNavigationProps) => {
  const { pathname } = useLocation();
  const { open: openOffers } = useDialog("forfait");

  const handleOpenOffers = () => {
    openOffers("create", {
      id: "forfait",
    });
  };

  return (
    <>
      <nav
        aria-label="Navigation mobile principale"
        className={cn(
          "fixed inset-x-3 bottom-3 z-40 lg:hidden",
          "grid grid-cols-4 items-center",
          "rounded-3xl border border-slate-200 bg-white/95",
          "px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2",
          "shadow-[0_18px_50px_rgba(15,23,42,0.18)]",
          "backdrop-blur-xl",
        )}
      >
        {mobileTabs.map((tab) => {
          const IconComponent = tab.icon;
          const isActive = isTabActive(pathname, tab, menuOpen);

          const tabClassName = cn(
            "flex min-h-12 flex-col items-center justify-center gap-1",
            "rounded-2xl px-1 text-[11px] font-semibold",
            "transition-colors focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-indigo-500",
            isActive
              ? "bg-indigo-50 text-indigo-700"
              : "text-slate-500 hover:bg-slate-50 hover:text-slate-900",
          );

          if (tab.action === "open-menu") {
            return (
              <button
                key={tab.label}
                type="button"
                onClick={() => onMenuOpenChange(true)}
                aria-label="Afficher toutes les rubriques"
                aria-expanded={menuOpen}
                className={tabClassName}
              >
                <IconComponent aria-hidden size={23} weight={isActive ? "fill" : "bold"} />

                <span>{tab.label}</span>
              </button>
            );
          }

          return (
            <Link
              key={tab.label}
              to={tab.path as string}
              aria-current={isActive ? "page" : undefined}
              className={tabClassName}
            >
              <IconComponent aria-hidden size={21} weight={isActive ? "fill" : "regular"} />

              <span className="max-w-full truncate">{tab.label}</span>
            </Link>
          );
        })}
      </nav>

      <Sheet open={menuOpen} onOpenChange={onMenuOpenChange}>
        <SheetContent
          side="bottom"
          showClose={false}
          className={cn(
            "max-h-[88dvh] overflow-hidden rounded-t-[2rem]",
            "border-slate-200 bg-white p-0 shadow-2xl",
          )}
        >
          <div aria-hidden className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-slate-300" />

          <SheetHeader className="flex-row items-start justify-between space-y-0 px-5 pb-4 pt-5 text-left">
            <div>
              <SheetTitle className="text-xl font-bold text-indigo-950">
                Toutes les rubriques
              </SheetTitle>

              <SheetDescription className="mt-1 text-sm text-slate-500">
                Accédez rapidement à votre espace monCVpro.
              </SheetDescription>
            </div>

            <SheetClose
              aria-label="Fermer le menu"
              className={cn(
                "grid size-10 shrink-0 place-items-center rounded-full",
                "bg-slate-100 text-slate-600 transition-colors",
                "hover:bg-slate-200 hover:text-slate-950",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-indigo-500",
              )}
            >
              <XIcon aria-hidden size={20} />
            </SheetClose>
          </SheetHeader>

          <div
            className={cn(
              "max-h-[calc(88dvh-7rem)] space-y-6 overflow-y-auto",
              "px-4 pb-[calc(env(safe-area-inset-bottom)+2rem)]",
            )}
          >
            {USER_SPACE_NAVIGATION.map((section, sectionIndex) => (
              <section key={section.label ?? `section-${sectionIndex}`}>
                {section.label && (
                  <h2 className="mb-2 px-1 text-xs font-bold uppercase tracking-wider text-slate-400">
                    {section.label}
                  </h2>
                )}

                <div className="grid gap-2 sm:grid-cols-2">
                  {section.items.map((item) => (
                    <MenuItem key={item.path} item={item} pathname={pathname} />
                  ))}
                </div>
              </section>
            ))}

            <section>
              <h2 className="mb-2 px-1 text-xs font-bold uppercase tracking-wider text-slate-400">
                Mon compte
              </h2>

              <div className="grid gap-2 sm:grid-cols-2">
                <SheetClose asChild>
                  <Link
                    to={USER_SPACE_PATHS.wallet}
                    className={cn(
                      "flex min-h-16 items-center gap-3 rounded-2xl",
                      "border border-slate-100 bg-white px-4",
                      "text-slate-700 transition-colors hover:bg-slate-50",
                      "focus-visible:outline-none focus-visible:ring-2",
                      "focus-visible:ring-indigo-500",
                    )}
                  >
                    <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
                      <WalletIcon aria-hidden size={21} weight="duotone" />
                    </span>

                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">Portefeuille</span>

                      <span className="block text-xs text-slate-500">Consulter mon solde</span>
                    </span>
                  </Link>
                </SheetClose>

                <SheetClose asChild>
                  <Link
                    to={USER_SPACE_PATHS.help}
                    className={cn(
                      "flex min-h-16 items-center gap-3 rounded-2xl",
                      "border border-slate-100 bg-white px-4",
                      "text-slate-700 transition-colors hover:bg-slate-50",
                      "focus-visible:outline-none focus-visible:ring-2",
                      "focus-visible:ring-indigo-500",
                    )}
                  >
                    <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-indigo-50 text-indigo-600">
                      <HeadsetIcon aria-hidden size={21} weight="duotone" />
                    </span>

                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">Centre d’aide</span>

                      <span className="block text-xs text-slate-500">Obtenir de l’assistance</span>
                    </span>
                  </Link>
                </SheetClose>

                <SheetClose asChild>
                  <button
                    type="button"
                    onClick={handleOpenOffers}
                    className={cn(
                      "flex min-h-16 items-center gap-3 rounded-2xl",
                      "border border-indigo-200 bg-indigo-50 px-4",
                      "text-indigo-700 transition-colors hover:bg-indigo-100",
                      "focus-visible:outline-none focus-visible:ring-2",
                      "focus-visible:ring-indigo-500",
                    )}
                  >
                    <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-indigo-600 text-white">
                      <SparkleIcon aria-hidden size={21} weight="fill" />
                    </span>

                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">Découvrir les offres</span>

                      <span className="block text-xs text-indigo-500">
                        Voir les formules disponibles
                      </span>
                    </span>
                  </button>
                </SheetClose>
              </div>
            </section>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
